import React, { useState } from 'react';
import { clsx } from 'clsx';
import { FileSpreadsheet, Loader2 } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { Dropzone, type PickedFile } from '@components/Dropzone';
import importService, { validateImportFile } from '@services/importService';
import {
  IMPORT_DATE_FORMATS,
  IMPORT_DELIMITERS,
  IMPORT_TARGET_TYPE_LABELS,
  type ImportDateFormat,
  type ImportLayout,
  type ImportLayoutInput,
  type ImportSample,
  type ImportTargetType,
} from '@/types/finance';
import { ApiError } from '@/utils/apiError';
import { Required, labelCls } from './financeBits';

interface Props {
  layout?: ImportLayout | null;
  onClose: () => void;
  onSaved: (layout: ImportLayout) => void;
  onError: (err: unknown) => void;
}

type ColumnField = 'date_column' | 'description_column' | 'amount_column' | 'credit_column';

const COLUMN_FIELDS: { key: ColumnField; label: string; required: boolean; hint: string }[] = [
  { key: 'date_column', label: 'Data', required: true, hint: 'coluna com a data do lançamento' },
  { key: 'description_column', label: 'Descrição', required: true, hint: 'histórico / lançamento' },
  { key: 'amount_column', label: 'Valor', required: true, hint: 'valor com sinal — ou só o débito, se o crédito vier separado' },
  { key: 'credit_column', label: 'Crédito (opcional)', required: false, hint: 'quando o banco separa crédito e débito em duas colunas' },
];

/**
 * Editor de layout CSV/XLSX (POST/PUT /import-layouts): sobe um arquivo de exemplo
 * (POST /imports/sample) e mostra as primeiras 15 linhas em colunas numeradas 0..N para o usuário
 * apontar data, descrição, valor e crédito; delimitador, cabeçalho, linhas a pular, formato de
 * data, vírgula decimal e inversão de sinal.
 */
export const ImportLayoutFormModal: React.FC<Props> = ({ layout, onClose, onSaved, onError }) => {
  const isEdit = !!layout;
  const [saving, setSaving] = useState(false);
  const [sampling, setSampling] = useState(false);
  const [sample, setSample] = useState<ImportSample | null>(null);
  const [sampleError, setSampleError] = useState<string | null>(null);
  const [form, setForm] = useState<ImportLayoutInput>(() => ({
    name: layout?.name ?? '',
    target_type: layout?.target_type ?? 'account',
    file_format: layout?.file_format ?? 'csv',
    delimiter: layout?.delimiter ?? ';',
    has_header: layout?.has_header ?? true,
    skip_rows: layout?.skip_rows ?? 0,
    date_column: layout?.date_column ?? 0,
    description_column: layout?.description_column ?? 1,
    amount_column: layout?.amount_column ?? 2,
    credit_column: layout?.credit_column ?? null,
    date_format: layout?.date_format ?? '02/01/2006',
    decimal_comma: layout?.decimal_comma ?? true,
    invert_sign: layout?.invert_sign ?? false,
  }));

  const set = <K extends keyof ImportLayoutInput>(key: K, value: ImportLayoutInput[K]) => setForm((f) => ({ ...f, [key]: value }));

  const columnCount = sample ? Math.max(0, ...sample.rows.map((r) => r.length)) : 0;
  const columnOptions = Array.from({ length: Math.max(columnCount, 12) }, (_, i) => i);

  const loadSample = async (files: PickedFile[]) => {
    const file = files[0]?.file;
    if (!file) return;
    const problem = validateImportFile(file);
    if (problem) {
      setSampleError(problem);
      return;
    }
    setSampling(true);
    setSampleError(null);
    try {
      const s = await importService.sample(file);
      setSample(s);
      if (s.file_format === 'csv' || s.file_format === 'xlsx') set('file_format', s.file_format);
      if (s.file_format === 'ofx') setSampleError('Arquivo OFX não precisa de layout: a importação já o entende sozinha.');
      if (s.delimiter && IMPORT_DELIMITERS.some((d) => d.value === s.delimiter)) set('delimiter', s.delimiter);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) setSampleError(err.message);
      else onError(err);
    } finally {
      setSampling(false);
    }
  };

  const canSubmit =
    !saving &&
    !!form.name.trim() &&
    form.date_column >= 0 &&
    form.description_column >= 0 &&
    form.amount_column >= 0 &&
    new Set([form.date_column, form.description_column, form.amount_column]).size === 3;

  const save = async () => {
    if (!canSubmit) return;
    const payload: ImportLayoutInput = {
      ...form,
      name: form.name.trim(),
      delimiter: form.file_format === 'csv' ? form.delimiter || ';' : null,
      credit_column: form.credit_column === null || form.credit_column === undefined || Number.isNaN(form.credit_column) ? null : form.credit_column,
    };
    setSaving(true);
    try {
      onSaved(layout ? await importService.updateLayout(layout.id, payload) : await importService.createLayout(payload));
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const roleOf = (col: number): string[] =>
    COLUMN_FIELDS.filter((f) => form[f.key] === col).map((f) => f.label.replace(' (opcional)', ''));

  /** Texto do cabeçalho da coluna na amostra (linha logo após as puladas), quando há cabeçalho. */
  const headerCell = (col: number): string => (sample && form.has_header ? sample.rows[form.skip_rows]?.[col] ?? '' : '');

  return (
    <Modal isOpen onClose={onClose} size="xl" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <FileSpreadsheet size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar layout' : 'Novo layout de importação'}</h2>
      </div>
      <div className="px-6 py-4 max-h-[75vh] overflow-y-auto custom-scrollbar space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>
              Nome
              <Required />
            </label>
            <input className="input-field" maxLength={100} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex.: Itaú CSV corrente" autoFocus />
          </div>
          <div>
            <label className={labelCls}>Para</label>
            <select className="select-field" value={form.target_type} onChange={(e) => set('target_type', e.target.value as ImportTargetType)}>
              <option value="account">{IMPORT_TARGET_TYPE_LABELS.account}</option>
              <option value="card">{IMPORT_TARGET_TYPE_LABELS.card}</option>
            </select>
          </div>
        </div>

        {/* Amostra */}
        <section className="space-y-2">
          <p className="text-label">Arquivo de exemplo</p>
          <Dropzone
            onFiles={loadSample}
            accept=".csv,.xlsx,.txt"
            multiple={false}
            disabled={sampling || saving}
            title={sample ? 'Trocar o arquivo de exemplo' : 'Arraste um CSV ou XLSX do banco aqui ou clique para escolher'}
            hint="Só as 15 primeiras linhas são lidas, para você apontar as colunas. Nada é importado agora."
            className="py-5"
          />
          {sampling && (
            <p className="text-small">
              <Loader2 size={14} className="inline animate-spin mr-1" /> Lendo o arquivo…
            </p>
          )}
          {sampleError && <p className="text-sm text-red-600">{sampleError}</p>}
          {sample && sample.rows.length > 0 && (
            <div className="rounded-lg border border-slate-200 overflow-x-auto max-h-64 custom-scrollbar">
              <table className="text-xs min-w-max">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-1 text-left text-slate-400 font-medium">#</th>
                    {Array.from({ length: columnCount }, (_, i) => (
                      <th key={i} className={clsx('px-2 py-1 text-left font-semibold whitespace-nowrap', roleOf(i).length > 0 ? 'text-emerald-700' : 'text-slate-600')}>
                        {i}
                        {roleOf(i).length > 0 && <span className="ml-1 font-normal">({roleOf(i).join(', ')})</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sample.rows.map((row, ri) => (
                    <tr key={ri} className={clsx('border-t border-slate-100', ri < form.skip_rows && 'opacity-40 line-through', ri === form.skip_rows && form.has_header && 'bg-amber-50')}>
                      <td className="px-2 py-1 text-slate-400">{ri}</td>
                      {Array.from({ length: columnCount }, (_, ci) => (
                        <td key={ci} className={clsx('px-2 py-1 max-w-[16rem] truncate', roleOf(ci).length > 0 && 'bg-emerald-50/60')} title={row[ci]}>
                          {row[ci] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Colunas */}
        <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {COLUMN_FIELDS.map((f) => (
            <div key={f.key}>
              <label className={labelCls} title={f.hint}>
                {f.label}
                {f.required && <Required />}
              </label>
              <select
                className="select-field"
                value={form[f.key] === null || form[f.key] === undefined ? '' : String(form[f.key])}
                onChange={(e) => set(f.key, e.target.value === '' ? (f.required ? 0 : null) : Number(e.target.value))}
              >
                {!f.required && <option value="">Não há</option>}
                {columnOptions.map((i) => (
                  <option key={i} value={i}>
                    Coluna {i}
                    {headerCell(i) ? ` — ${headerCell(i)}` : ''}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </section>

        {/* Formato */}
        <section className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Formato do arquivo</label>
            <select className="select-field" value={form.file_format} onChange={(e) => set('file_format', e.target.value as 'csv' | 'xlsx')}>
              <option value="csv">CSV</option>
              <option value="xlsx">XLSX</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Delimitador (CSV)</label>
            <select className="select-field" value={form.delimiter ?? ';'} disabled={form.file_format !== 'csv'} onChange={(e) => set('delimiter', e.target.value)}>
              {IMPORT_DELIMITERS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Formato da data</label>
            <select className="select-field" value={form.date_format} onChange={(e) => set('date_format', e.target.value as ImportDateFormat)}>
              {IMPORT_DATE_FORMATS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Linhas a pular no início</label>
            <input type="number" className="input-field" min={0} max={50} value={form.skip_rows} onChange={(e) => set('skip_rows', Math.max(0, Math.trunc(Number(e.target.value) || 0)))} />
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={form.has_header} onChange={(e) => set('has_header', e.target.checked)} />
            Primeira linha é cabeçalho
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={form.decimal_comma} onChange={(e) => set('decimal_comma', e.target.checked)} />
            Vírgula decimal (1.234,56)
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer sm:col-span-2">
            <input type="checkbox" className="checkbox-field" checked={form.invert_sign} onChange={(e) => set('invert_sign', e.target.checked)} />
            Inverter o sinal (o arquivo traz despesas positivas)
          </label>
        </section>
        {!canSubmit && form.name.trim() && <p className="text-sm text-amber-700">Data, descrição e valor precisam ser colunas diferentes.</p>}
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Criar layout'}
        </Button>
      </div>
    </Modal>
  );
};
